import React from "react";
import { type FieldPath, type FieldValues, useController } from "react-hook-form";
import { RegrelloFormFieldDate, type RegrelloFormFieldDatePropsV2 } from "../RegrelloFormFieldDate";
import type { FormFieldPropsToOmit } from "./types";
import { getErrorProp, type RegrelloControlledFormFieldProps, useInvokeOnChangeOnRequiredStateChange } from "./utils";

/**
 * A controlled date field for use with react-hook-form.
 */
function RegrelloControlledFormFieldDateInternal<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>, // TODO: Change to PathToValueOfType<TFieldValues, Date | string | null>
>({
  controllerProps,
  isDefaultTimeStartOfDay = false,
  onValueChange,
  showTimePicker = false,
  ...dateFieldProps
}: RegrelloControlledFormFieldProps<TFieldValues, TName, Omit<RegrelloFormFieldDatePropsV2, FormFieldPropsToOmit>> & {
  /**
   * Capture the change event before the rerender happens. This is useful for setting the
   * "useForm" form values to the correct type before rendering a new RegrelloFormField of a
   * different type. This avoids passing in a incorrect data type to the component.
   */
  onValueChange?: (name: string, newValue: Date | string | null) => void;

  /**
   * Whether to show a time-picker with the date-picker.
   *
   * @default false
   */
  showTimePicker?: boolean;
}) {
  const { field, fieldState } = useController(controllerProps);
  const { ref, ...fieldProps } = field;

  useInvokeOnChangeOnRequiredStateChange(field, dateFieldProps.isRequiredAsteriskShown ?? false);

  return (
    <RegrelloFormFieldDate
      {...fieldProps}
      {...dateFieldProps}
      {...getErrorProp(fieldState)}
      firstSegmentRef={ref} // (clewis): This is the element that needs to be programmatically focusable when this field is missing a value when the form is submitted.
      inputRef={dateFieldProps.inputRef}
      isDefaultTimeStartOfDay={isDefaultTimeStartOfDay}
      onChange={(newDate) => {
        onValueChange?.(field.name, newDate);
        fieldProps.onChange(newDate);
      }}
      showTimePicker={showTimePicker}
    />
  );
}

export const RegrelloControlledFormFieldDate = React.memo(
  RegrelloControlledFormFieldDateInternal,
) as typeof RegrelloControlledFormFieldDateInternal;
